/**
 * ADR-0016 — Deviation detectors (lots vs movements vs snapshots).
 * Read-only; for Safe Mode / recovery (ADR-0012), not day-to-day truth writes.
 */

import { sameLotId } from '../../../utils/productIdUtils';

const ADD_LIKE = new Set([
  'PURCHASE',
  'SALE_RETURN',
  'TRANSFER_IN',
  'PRODUCTION_OUTPUT'
]);
const SUB_LIKE = new Set([
  'SALE',
  'TRANSFER_OUT',
  'EXPIRY',
  'CONSUMPTION',
  'PRODUCTION_CONSUME'
]);

function listLots(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === 'object') return Object.values(raw).filter(Boolean);
  return [];
}

function listMovements(parsed) {
  const active = Array.isArray(parsed?.stockMovements) ? parsed.stockMovements : [];
  const archive = Array.isArray(parsed?.stockMovementsArchive)
    ? parsed.stockMovementsArchive
    : [];
  return [...active, ...archive].filter(Boolean);
}

/**
 * Net signed effect of one movement on lot remaining (mirrors projection rules).
 */
export function signedMovementEffectOnLotRemaining(movement) {
  if (!movement) return 0;
  const type = String(movement.movementType || '');
  const signed = Number(movement.quantity);
  if (!Number.isFinite(signed) || signed === 0) return 0;
  if (type === 'ADJUSTMENT' || type === 'VOID') return signed;
  if (ADD_LIKE.has(type)) return Math.abs(signed);
  if (SUB_LIKE.has(type)) return -Math.abs(signed);
  return signed;
}

/**
 * Sum movement signed qty per lotId (all ledger partitions).
 * @param {object} parsed
 * @returns {Map<string, number>}
 */
export function sumMovementQtyByLotId(parsed) {
  const map = new Map();
  for (const m of listMovements(parsed)) {
    if (m.lotId == null || m.lotId === '') continue;
    const key = String(m.lotId);
    const effect = signedMovementEffectOnLotRemaining(m);
    map.set(key, (map.get(key) || 0) + effect);
  }
  return map;
}

/**
 * Compare lot.remainingQuantity with net movement sum for the same lotId.
 * zeroLotTolerance: lots with rem≈0 and no movements are not reported.
 *
 * @param {object} parsed
 * @param {{ epsilon?: number }} [options]
 */
export function detectLotVsMovementDeviation(parsed, options = {}) {
  const epsilon = options.epsilon != null ? Number(options.epsilon) : 1e-3;
  const lots = listLots(parsed?.lots);
  const moveByLot = sumMovementQtyByLotId(parsed);
  const deviations = [];

  for (const lot of lots) {
    if (lot?.id == null) continue;
    const key = String(lot.id);
    const rem = Number(lot.remainingQuantity) || 0;
    const fromMoves = moveByLot.get(key);
    if (fromMoves == null) {
      if (Math.abs(rem) > epsilon) {
        deviations.push({
          lotId: key,
          productId: lot.productId,
          remaining: rem,
          movementNet: null,
          delta: rem,
          reason: 'lot_without_movements'
        });
      }
      continue;
    }
    const delta = rem - fromMoves;
    if (Math.abs(delta) > epsilon) {
      deviations.push({
        lotId: key,
        productId: lot.productId,
        remaining: rem,
        movementNet: fromMoves,
        delta,
        reason: 'remaining_vs_movement_net'
      });
    }
  }

  // Movements that point at missing lots with non-zero net
  for (const [key, net] of moveByLot) {
    if (Math.abs(net) <= epsilon) continue;
    const lot = lots.find((l) => sameLotId(l?.id, key));
    if (!lot) {
      deviations.push({
        lotId: key,
        productId: null,
        remaining: null,
        movementNet: net,
        delta: -net,
        reason: 'movement_without_lot'
      });
    }
  }

  return {
    ok: deviations.length === 0,
    deviationCount: deviations.length,
    deviations
  };
}

/**
 * Snapshot on-hand total that should equal sum of lot.remainingQuantity.
 * ADR-0016 buckets: current + damaged + expired + inTransit (reserved stays inside current).
 * Comparing only `current` falsely trips Safe Mode whenever any expired/transit stock exists.
 *
 * @param {object} snap
 * @returns {number}
 */
export function snapshotOnHandTotal(snap) {
  if (!snap || typeof snap !== 'object') return 0;
  const current = Number(snap.current ?? snap.quantity ?? 0) || 0;
  const damaged = Number(snap.damaged) || 0;
  const expired = Number(snap.expired) || 0;
  const inTransit = Number(snap.inTransit) || 0;
  return current + damaged + expired + inTransit;
}

/**
 * Compare live lot sum (product@location) with stockSnapshots on-hand buckets.
 * @param {object} parsed
 * @param {{ epsilon?: number }} [options]
 */
export function detectLotVsSnapshotDeviation(parsed, options = {}) {
  const epsilon = options.epsilon != null ? Number(options.epsilon) : 1e-3;
  const lots = listLots(parsed?.lots);
  const snaps = Array.isArray(parsed?.stockSnapshots) ? parsed.stockSnapshots : [];
  const sumFromLots = new Map();

  for (const lot of lots) {
    if (lot?.productId == null) continue;
    const loc = String(lot.location || 'Principal');
    const key = `${String(lot.productId)}|${loc}`;
    sumFromLots.set(key, (sumFromLots.get(key) || 0) + (Number(lot.remainingQuantity) || 0));
  }

  const deviations = [];
  const seenSnap = new Set();

  for (const snap of snaps) {
    if (snap?.productId == null) continue;
    const loc = String(snap.location || snap.locationId || 'Principal');
    const key = `${String(snap.productId)}|${loc}`;
    seenSnap.add(key);
    const onHand = snapshotOnHandTotal(snap);
    const fromLots = sumFromLots.get(key) || 0;
    const delta = fromLots - onHand;
    if (Math.abs(delta) > epsilon) {
      deviations.push({
        productId: snap.productId,
        location: loc,
        lotRemainingSum: fromLots,
        snapshotCurrent: Number(snap.current ?? snap.quantity ?? 0) || 0,
        snapshotOnHand: onHand,
        delta,
        reason: 'lots_vs_snapshot'
      });
    }
  }

  for (const [key, fromLots] of sumFromLots) {
    if (seenSnap.has(key)) continue;
    if (Math.abs(fromLots) <= epsilon) continue;
    const [productId, location] = key.split('|');
    deviations.push({
      productId,
      location,
      lotRemainingSum: fromLots,
      snapshotCurrent: null,
      snapshotOnHand: null,
      delta: fromLots,
      reason: 'lot_without_snapshot'
    });
  }

  return {
    ok: deviations.length === 0,
    deviationCount: deviations.length,
    deviations
  };
}

/**
 * Combined inventory truth check for Safe Mode dashboards.
 */
export function detectInventoryLedgerDeviations(parsed, options = {}) {
  const lotsVsMoves = detectLotVsMovementDeviation(parsed, options);
  const lotsVsSnaps = detectLotVsSnapshotDeviation(parsed, options);
  return {
    ok: lotsVsMoves.ok && lotsVsSnaps.ok,
    lotsVsMoves,
    lotsVsSnaps,
    deviationCount: lotsVsMoves.deviationCount + lotsVsSnaps.deviationCount
  };
}
